import React, { useEffect, useState, useMemo } from "react";
import { Card, Progress, Typography, Spin, Space, Divider, Input, Radio, Tag, Row, Col, Empty, Button } from "antd";
import { SearchOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { get_qouta_usage } from "../../api/admin.apis";

const { Title, Text, Paragraph } = Typography;

/**
 * GA4 official quota limits
 */
const GA4_QUOTA_LIMITS = {
  tokensPerDay: 200000,
  tokensPerHour: 40000,
  tokensPerProjectPerHour: 14000,
  concurrentRequests: 7,
  serverErrorsPerProjectPerHour: 10,
  potentiallyThresholdedRequestsPerHour: 75,
};

/**
 * Calculate consumed quota from remaining
 */
const calculateConsumed = (limit, remaining) => {
  if (remaining === undefined || remaining === null) return 0;
  return Math.max(limit - remaining, 0);
};

/**
 * Calculate usage percentage
 */
const calculatePercentage = (remaining, limit) => {
  const consumed = calculateConsumed(limit, remaining);
  return Math.min(Math.round((consumed / limit) * 100), 100);
};

/**
 * Determine quota status based on percentage
 */
const getQuotaStatus = (percent) => {
  if (percent >= 90) return "exception";
  if (percent >= 75) return "active";
  return "success";
};

/**
 * Quota progress row
 */
const QuotaRow = ({ label, remaining, limit, showStatus = true }) => {
  const consumed = calculateConsumed(limit, remaining);
  const percent = calculatePercentage(remaining, limit);
  const status = showStatus ? getQuotaStatus(percent) : "normal";

  return (
    <Space direction="vertical" style={{ width: "100%" }} size={4}>
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Text>{label}</Text>
        <Text type="secondary">
          {consumed.toLocaleString()} / {remaining?.toLocaleString() || 0} used
        </Text>
      </Space>
      <Progress 
        percent={percent} 
        status={status} 
        showInfo={false}
        strokeColor={
          percent >= 90 ? '#ff4d4f' : 
          percent >= 75 ? '#faad14' : 
          '#52c41a'
        }
      />
      <Text type="secondary" style={{ fontSize: 11 }}>
        {percent}% consumed • {remaining?.toLocaleString()} remaining
      </Text>
    </Space>
  );
};

/**
 * Main component
 */
const QuotaUsageCard = () => {
  const [loading, setLoading] = useState(true);
  const [quotas, setQuotas] = useState([]); // Array for multiple properties
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('a'); // 'a' for all, 'b' for high usage (>75%), 'c' for critical (>90%)

  useEffect(() => {
    const loadQuota = async () => {
      try {
        const res = await get_qouta_usage();
        const data = res?.data?.data || [];
        setQuotas(data.filter(item => item.property_quota));
      } catch (error) {
        console.error("Failed to load quota usage", error);
      } finally {
        setLoading(false);
      }
    };

    loadQuota();
  }, []);

  // Calculate usage percentage for each property
  const quotasWithUsage = useMemo(() => {
    return quotas.map(item => {
      const quota = item.property_quota;
      
      // Calculate overall usage percentage (average of all quota types)
      const dayPercent = calculatePercentage(quota.tokensPerDay?.remaining, GA4_QUOTA_LIMITS.tokensPerDay);
      const hourPercent = calculatePercentage(quota.tokensPerHour?.remaining, GA4_QUOTA_LIMITS.tokensPerHour);
      const projectHourPercent = calculatePercentage(quota.tokensPerProjectPerHour?.remaining, GA4_QUOTA_LIMITS.tokensPerProjectPerHour);
      
      // Use the highest percentage as the overall status
      const maxPercent = Math.max(dayPercent, hourPercent, projectHourPercent);
      
      return {
        ...item,
        usagePercent: maxPercent,
        dayPercent,
        hourPercent,
        projectHourPercent,
        // Create searchable text combining property name and ID
        searchableText: [
          item.property_name,
          item.property_id,
          item.user_id
        ].filter(Boolean).join(' ').toLowerCase()
      };
    });
  }, [quotas]);

  // Filter data based on search and status
  const filteredQuotas = useMemo(() => {
    let filtered = quotasWithUsage;

    // Apply status filter
    if (filterStatus === 'b') {
      // High usage (>75%)
      filtered = filtered.filter(item => item.usagePercent >= 75 && item.usagePercent < 90);
    } else if (filterStatus === 'c') {
      // Critical usage (>=90%)
      filtered = filtered.filter(item => item.usagePercent >= 90);
    }

    // Apply search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item => {
        // Search in property name
        const propertyNameMatch = item.property_name?.toLowerCase().includes(searchLower);
        // Search in property ID
        const propertyIdMatch = item.property_id?.toLowerCase().includes(searchLower);
        // Search in combined searchable text
        const searchableMatch = item.searchableText?.includes(searchLower);
        
        return propertyNameMatch || propertyIdMatch || searchableMatch;
      });
    }

    return filtered;
  }, [quotasWithUsage, searchText, filterStatus]);

  // Calculate counts for radio buttons
  const totalCount = quotasWithUsage.length;
  const highUsageCount = quotasWithUsage.filter(item => item.usagePercent >= 75 && item.usagePercent < 90).length;
  const criticalCount = quotasWithUsage.filter(item => item.usagePercent >= 90).length;

  const handleStatusFilterChange = (e) => {
    setFilterStatus(e.target.value);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const clearFilters = () => {
    setSearchText('');
    setFilterStatus('a');
  };

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Spin size="large" />
          <Paragraph style={{ marginTop: 16 }}>Loading quota usage data...</Paragraph>
        </div>
      </Card>
    );
  }

  if (!quotas.length) {
    return (
      <Card>
        <Empty
          description="No quota data available"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Header Row with Title, Search, and Radio */}
        <Row gutter={[16, 16]} align="middle">
          {/* Title Column */}
          <Col xs={24} sm={24} md={8}>
            <Space direction="vertical" size={0}>
              <Title level={5} style={{ margin: 0 }}>GA4 API Quota Usage</Title>
              <Paragraph type="secondary" style={{ margin: 0 }}>
                Properties: <strong>{totalCount}</strong>
                {(searchText || filterStatus !== 'a') && (
                  <span> • Filtered: <strong>{filteredQuotas.length}</strong></span>
                )}
              </Paragraph>
            </Space>
          </Col>

          {/* Search Column - Updated placeholder */}
          <Col xs={24} sm={24} md={8}>
            <Input
              placeholder="Search by property name or ID..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearchChange}
              allowClear
            />
          </Col>

          {/* Radio Group Column */}
          <Col xs={24} sm={24} md={8}>
            <div style={{ textAlign: 'right' }}>
              <Radio.Group 
                value={filterStatus} 
                onChange={handleStatusFilterChange}
                optionType="button"
                buttonStyle="solid"
                size="middle"
              >
                <Radio.Button value="a">ALL ({totalCount})</Radio.Button>
                <Radio.Button value="b">HIGH USAGE ({highUsageCount})</Radio.Button>
                <Radio.Button value="c">CRITICAL ({criticalCount})</Radio.Button>
              </Radio.Group>
            </div>
          </Col>
        </Row>

        {/* Active Filters Display */}
        {(searchText || filterStatus !== 'a') && (
          <Row>
            <Col span={24}>
              <Space size={8} wrap>
                <Text type="secondary" style={{ fontSize: 12 }}>Active filters:</Text>
                {filterStatus !== 'a' && (
                  <Tag 
                    closable 
                    onClose={() => setFilterStatus('a')}
                    style={{ marginRight: 0 }}
                  >
                    Status: {filterStatus === 'b' ? 'High Usage (>75%)' : 'Critical (≥90%)'}
                  </Tag>
                )}
                {searchText && (
                  <Tag 
                    closable 
                    onClose={() => setSearchText('')}
                    style={{ marginRight: 0 }}
                  >
                    Search: "{searchText}"
                  </Tag>
                )}
                {(searchText || filterStatus !== 'a') && (
                  <Button 
                    type="link" 
                    size="small" 
                    onClick={clearFilters}
                    style={{ padding: 0 }}
                  >
                    Clear all
                  </Button>
                )}
              </Space>
            </Col>
          </Row>
        )}

        {/* Content Area */}
        {filteredQuotas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Title level={4}>
              {searchText || filterStatus !== 'a' ? 'No Matching Results Found' : 'No Quota Data Available'}
            </Title>
            <Paragraph type="secondary">
              {searchText && `No properties match "${searchText}". `}
              {filterStatus === 'b' && 'No properties with high usage found. '}
              {filterStatus === 'c' && 'No properties with critical usage found. '}
              Try adjusting your filters.
            </Paragraph>
            {(searchText || filterStatus !== 'a') && (
              <Button onClick={clearFilters} type="primary">
                Clear All Filters
              </Button>
            )}
          </div>
        ) : (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {filteredQuotas.map((item, index) => {
              const quota = item.property_quota;
              const dayPercent = calculatePercentage(quota.tokensPerDay?.remaining, GA4_QUOTA_LIMITS.tokensPerDay);
              const hourPercent = calculatePercentage(quota.tokensPerHour?.remaining, GA4_QUOTA_LIMITS.tokensPerHour);
              const projectHourPercent = calculatePercentage(quota.tokensPerProjectPerHour?.remaining, GA4_QUOTA_LIMITS.tokensPerProjectPerHour);

              return (
                <Card 
                  key={item.property_id || index}
                  size="small"
                  style={{ 
                    borderLeft: dayPercent >= 90 ? '4px solid #ff4d4f' : 
                               dayPercent >= 75 ? '4px solid #faad14' : 
                               '4px solid #52c41a'
                  }}
                >
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    {/* Property Header - Now showing property name prominently */}
                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space direction="vertical" size={2}>
                          <Space>
                            <Title level={5} style={{ margin: 0 }}>
                              {item.property_name || 'Unnamed Property'}
                            </Title>
                            <Tag color={
                              item.usagePercent >= 90 ? 'error' :
                              item.usagePercent >= 75 ? 'warning' :
                              'success'
                            }>
                              {item.usagePercent >= 90 ? 'Critical' :
                               item.usagePercent >= 75 ? 'High Usage' :
                               'Healthy'}
                            </Tag>
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Property ID: {item.property_id}
                          </Text>
                        </Space>
                      </Col>
                      <Col>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <InfoCircleOutlined /> Overall Usage: {item.usagePercent}%
                        </Text>
                      </Col>
                    </Row>

                    {/* Quota Rows */}
                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                      <QuotaRow
                        label={`Tokens / Day (Limit: ${GA4_QUOTA_LIMITS.tokensPerDay.toLocaleString()})`}
                        remaining={quota.tokensPerDay?.remaining}
                        limit={GA4_QUOTA_LIMITS.tokensPerDay}
                      />

                      <Divider style={{ margin: '8px 0' }} />

                      <QuotaRow
                        label={`Tokens / Hour (Limit: ${GA4_QUOTA_LIMITS.tokensPerHour.toLocaleString()})`}
                        remaining={quota.tokensPerHour?.remaining}
                        limit={GA4_QUOTA_LIMITS.tokensPerHour}
                      />

                      <Divider style={{ margin: '8px 0' }} />

                      <QuotaRow
                        label={`Tokens / Project / Hour (Limit: ${GA4_QUOTA_LIMITS.tokensPerProjectPerHour.toLocaleString()})`}
                        remaining={quota.tokensPerProjectPerHour?.remaining}
                        limit={GA4_QUOTA_LIMITS.tokensPerProjectPerHour}
                      />
                    </Space>
                  </Space>
                </Card>
              );
            })}
          </Space>
        )}
      </Space>
    </Card>
  );
};

export default QuotaUsageCard;